/**
 * MobileAlunosList — Meus Alunos (professor), somente leitura + aviso ao responsável.
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Filter, X, ChevronRight, MessageCircle, Info } from 'lucide-react';
import { AvisarResponsavelModal } from './AvisarResponsavelModal';
import {
  TURNO_LABEL_ALUNOS,
  useMeusAlunosProfessorPage,
} from './useMeusAlunosProfessorPage';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';

function getInitials(nome: string): string {
  if (!nome) return '?';
  const parts = nome.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PAGE_SIZE = 20;

export function MobileAlunosList() {
  const navigate = useNavigate();
  const { alunos, turmasOpcoes, turnosDisponiveis, isLoading } = useMeusAlunosProfessorPage();

  const [search, setSearch] = useState('');
  const [filterTurma, setFilterTurma] = useState('');
  const [filterTurno, setFilterTurno] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [avisoAluno, setAvisoAluno] = useState<{ id: string; nome: string } | null>(null);

  const filtered = useMemo(() => {
    let list = alunos;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((a) =>
        [a.nome, a.turmaNome, a.matricula].join(' ').toLowerCase().includes(q),
      );
    }
    if (filterTurma) list = list.filter((a) => a.turmaId === filterTurma);
    if (filterTurno) list = list.filter((a) => a.turmaTurno === filterTurno);
    return list.sort((a, b) => {
      if (a.turmaNome !== b.turmaNome) return a.turmaNome.localeCompare(b.turmaNome, 'pt-BR');
      return (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
    });
  }, [alunos, search, filterTurma, filterTurno]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = visibleCount < filtered.length;

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount((v) => Math.min(v + PAGE_SIZE, filtered.length));
      }
    }, { rootMargin: '200px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, filtered.length]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, filterTurma, filterTurno]);

  const activeFilters = (filterTurma ? 1 : 0) + (filterTurno ? 1 : 0);
  const clearFilters = useCallback(() => {
    setFilterTurma('');
    setFilterTurno('');
    setShowFilters(false);
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, typeof visible>();
    visible.forEach((a) => {
      const key = a.turmaNome;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    });
    return map;
  }, [visible]);

  const hasFotoReal = (fotoUrl?: string) => {
    if (!fotoUrl) return false;
    const url = fotoUrl.toLowerCase();
    return !url.includes('i.pravatar.cc') && !url.includes('ui-avatars.com');
  };

  const badge =
    filtered.length > 0
      ? `${filtered.length} aluno${filtered.length !== 1 ? 's' : ''}`
      : undefined;

  return (
    <MobilePageWrapper
      title="Meus Alunos"
      icon={Users}
      iconColor="#93c5fd"
      hideBackButton
      badge={badge}
    >
      <div className="professor-alunos-root">
        <div className="professor-alunos-hint">
          <Info size={14} aria-hidden />
          <span>
            Toque no aluno para ver a ficha (somente leitura). Use o ícone de mensagem para{' '}
            <strong>avisar o responsável</strong>.
          </span>
        </div>

        <div className="professor-alunos-filter-bar">
          <div className="professor-alunos-search-wrap">
            <Search size={15} className="professor-alunos-search-icon" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar aluno..."
              className="professor-alunos-search-input"
              autoComplete="off"
            />
            {search && (
              <button
                type="button"
                className="professor-alunos-search-clear"
                onClick={() => setSearch('')}
                aria-label="Limpar busca"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            className={`professor-alunos-filter-btn${activeFilters > 0 ? ' active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Filtros"
          >
            <Filter size={15} />
            {activeFilters > 0 && (
              <span className="professor-alunos-filter-badge">{activeFilters}</span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="professor-alunos-filters-panel">
            <div className="professor-alunos-filter-group">
              <span className="professor-alunos-filter-label">Turma</span>
              <div className="professor-alunos-chips">
                <button
                  type="button"
                  className={`professor-alunos-chip${!filterTurma ? ' on' : ''}`}
                  onClick={() => setFilterTurma('')}
                >
                  Todas
                </button>
                {turmasOpcoes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`professor-alunos-chip${filterTurma === t.id ? ' on' : ''}`}
                    onClick={() => setFilterTurma(filterTurma === t.id ? '' : t.id)}
                  >
                    {t.nome}
                  </button>
                ))}
              </div>
            </div>
            {turnosDisponiveis.length > 1 && (
              <div className="professor-alunos-filter-group">
                <span className="professor-alunos-filter-label">Turno</span>
                <div className="professor-alunos-chips">
                  <button
                    type="button"
                    className={`professor-alunos-chip${!filterTurno ? ' on' : ''}`}
                    onClick={() => setFilterTurno('')}
                  >
                    Todos
                  </button>
                  {turnosDisponiveis.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`professor-alunos-chip${filterTurno === t ? ' on' : ''}`}
                      onClick={() => setFilterTurno(filterTurno === t ? '' : t)}
                    >
                      {TURNO_LABEL_ALUNOS[t] || t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeFilters > 0 && (
              <button type="button" className="professor-alunos-clear-filters" onClick={clearFilters}>
                Limpar filtros
              </button>
            )}
          </div>
        )}

        {isLoading && (
          <div className="professor-alunos-empty">Carregando...</div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="professor-alunos-empty">
            <Users size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
            <div className="professor-alunos-empty-title">Nenhum aluno encontrado</div>
            <div className="professor-alunos-empty-sub">
              {search || activeFilters > 0
                ? 'Tente ajustar a busca ou os filtros.'
                : 'Sem alunos nas suas turmas.'}
            </div>
          </div>
        )}

        {Array.from(groups.entries()).map(([turmaName, alunosGrupo]) => (
          <section key={turmaName} className="professor-alunos-group">
            <div className="professor-alunos-group-header">
              <span className="professor-alunos-group-title">{turmaName}</span>
              <span className="professor-alunos-group-line" />
              <span className="professor-alunos-group-count">{alunosGrupo.length}</span>
            </div>
            <ul className="professor-alunos-list">
              {alunosGrupo.map((aluno) => (
                <li key={aluno.id}>
                  <button
                    type="button"
                    className="professor-alunos-row"
                    onClick={() => navigate(`/professor/alunos/${aluno.id}`)}
                  >
                    {hasFotoReal(aluno.fotoUrl) ? (
                      <img
                        src={aluno.fotoUrl}
                        alt=""
                        className="professor-alunos-avatar professor-alunos-avatar--img"
                      />
                    ) : (
                      <span
                        className={`professor-alunos-avatar${
                          aluno.genero === 'FEMININO' ? ' professor-alunos-avatar--f' : ''
                        }`}
                      >
                        {getInitials(aluno.nome)}
                      </span>
                    )}
                    <span className="professor-alunos-row-info">
                      <span className="professor-alunos-row-name">{aluno.nome}</span>
                      <span className="professor-alunos-row-meta">
                        {aluno.matricula || '—'}
                        {aluno.turmaTurno
                          ? ` · ${TURNO_LABEL_ALUNOS[aluno.turmaTurno] || aluno.turmaTurno}`
                          : ''}
                      </span>
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="professor-alunos-aviso-btn"
                      title="Avisar responsável"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAvisoAluno({ id: aluno.id, nome: aluno.nome });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          setAvisoAluno({ id: aluno.id, nome: aluno.nome });
                        }
                      }}
                    >
                      <MessageCircle size={16} />
                    </span>
                    <ChevronRight size={16} className="professor-alunos-chevron" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {hasMore && (
          <div ref={sentinelRef} className="professor-alunos-loading" aria-hidden>
            <div className="professor-alunos-spinner" />
          </div>
        )}
      </div>

      {avisoAluno && (
        <AvisarResponsavelModal
          open={!!avisoAluno}
          onClose={() => setAvisoAluno(null)}
          alunoId={avisoAluno.id}
          alunoNome={avisoAluno.nome}
        />
      )}
    </MobilePageWrapper>
  );
}
